import { EMainProductCodeType } from '@aqx-core/common/api/config';
import dayjs, { getSecondsSinceEpoch } from '@aqx-core/common/dayjs';
import { convertToDisplayUTC } from '@aqx-core/common/hooks/timezone/use-display-utc-time';
import { TMixpanelProperties } from '@aqx-core/common/mixpanel/types';
import { companyViewController } from '@aqx-core/common/services/company/controller';
import { mapOrderTypeToOrderTypeLabel, mapOrderTypeToTextUpperCase } from '@aqx-core/common/utils/order';
import { mapFillPolicyIntToText } from '@aqx-core/common/utils/trade-form';

import { EMixpanelProperties } from '../../types';
import placeOrderHelper from '../PlaceOrderHelper';
import userHelper from '../UserHelper';
import {
  ICloseOrderProperties,
  ICreateBulkCloseOpenPendingPropertiesProps,
  ICreateBulkCloseOpenPendingSubmittedPropertiesProps,
  IDeleteOrderProperties
} from './type';

class CloseOrderHelper {
  public createBulkCloseOpenPendingSubmittedProperties = ({
    symbolDetails,
    numOfPositions,
    bulkCloseType
  }: ICreateBulkCloseOpenPendingSubmittedPropertiesProps): TMixpanelProperties => {
    const tradePreference = placeOrderHelper.createTradePreferenceProperties();
    const userAccountProperties = userHelper.createUserAccountProperties();
    const { symbol, currency } = symbolDetails || {};
    return {
      ...tradePreference,
      [EMixpanelProperties.BULK_CLOSE_TYPE]: bulkCloseType,
      [EMixpanelProperties.NUM_OF_POSITIONS_ALL]: numOfPositions ?? 0,
      [EMixpanelProperties.SYMBOL]: symbol,
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency,
      ...userAccountProperties
    };
  };

  public createBulkCloseOpenPendingProperties = ({
    symbol,
    numOfPositions,
    bulkCloseType
  }: ICreateBulkCloseOpenPendingPropertiesProps): TMixpanelProperties => {
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      [EMixpanelProperties.SYMBOL]: symbol,
      [EMixpanelProperties.NUM_OF_POSITIONS_ALL]: numOfPositions ?? 0,
      [EMixpanelProperties.BULK_CLOSE_TYPE]: bulkCloseType,
      ...userAccountProperties
    };
  };

  /** @ToDo Add profit loss */
  // eslint-disable-next-line complexity
  private closeOrderProperties = ({ values, order, livePrice }: ICloseOrderProperties): TMixpanelProperties => {
    // const profitTrackingOrders = orderService.getProfitTrackingOrders();
    // const currentOrderWithLiveProfit = profitTrackingOrders.find((o) => o.orderId === order.orderId);
    // const profitLoss = currentOrderWithLiveProfit?.profit;

    const { lotSize, fillPolicy } = values;

    const {
      symbol,
      currency,
      openTime,
      orderType,
      units,
      openPrice,
      currentPrice,
      takeProfit,
      stopLoss,
      swap,
      commission,
      lotSize: maxLotSize
    } = order;

    const convertedOpenTime = getSecondsSinceEpoch(
      companyViewController.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );

    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.SYMBOL_CURRENCY]: currency ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToTextUpperCase(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: maxLotSize ? maxLotSize : '-',
      [EMixpanelProperties.VOLUME_SIZE_SUBMITTED]: lotSize ? lotSize : '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? mapFillPolicyIntToText(fillPolicy, 'en_GB') : '-',
      [EMixpanelProperties.OPEN_PRICE]: openPrice ? openPrice : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? livePrice : currentPrice ?? '-',
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ? takeProfit : '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ? stopLoss : '-',
      [EMixpanelProperties.SWAP]: swap ? swap : '-',
      [EMixpanelProperties.COMMISSION]: commission ? commission : '-',
      [EMixpanelProperties.UNITS]: units ? units : '-'
      // [EMixpanelProperties.PROFIT_LOSS]: profitLoss ? profitLoss : '-'
    };
  };

  public createCloseOrderSubmittedProperties = ({
    values,
    order,
    livePrice
  }: ICloseOrderProperties): TMixpanelProperties => {
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    const closeOrderProperties = this.closeOrderProperties({ values, order, livePrice });
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      ...closeOrderProperties,
      ...tradePreferenceProperties,
      ...userAccountProperties
    };
  };

  // eslint-disable-next-line complexity
  private deleteOrderProperties = ({ order, livePrice }: IDeleteOrderProperties): TMixpanelProperties => {
    const {
      lotSize,
      fillPolicy,
      symbol,
      openTime,
      orderType,
      units,
      expiration,
      tradeExpiry,
      price, // Entry Price
      priceTrigger, // Stop Limit Price / Pending Price
      currentPrice,
      takeProfit,
      stopLoss,
      filledLotSize
    } = order;

    const isMT5 = companyViewController.getCompanyIntegration() === EMainProductCodeType.METATRADER5;

    const displayLotSize = () => {
      const filled = Number.isFinite(lotSize) ? lotSize : '-';
      if (isMT5) {
        const unfill = Number.isFinite(filledLotSize) ? filledLotSize : '-';
        return `${filled} / ${unfill}`;
      }
      return filled;
    };

    const convertedOpenTime = getSecondsSinceEpoch(
      companyViewController.convertToDayjsWithOffset({ input: openTime, keepTime: true })
    );

    const convertedExpiration = getSecondsSinceEpoch(
      companyViewController.convertToDayjsWithOffset({ input: expiration, keepTime: true })
    );

    return {
      [EMixpanelProperties.SYMBOL]: symbol ?? '-',
      [EMixpanelProperties.OPEN_TIME]: openTime
        ? convertToDisplayUTC(dayjs(convertedOpenTime).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.ORDER_TYPE]: orderType ? mapOrderTypeToOrderTypeLabel(orderType) : '-',
      [EMixpanelProperties.LOT_SIZE]: displayLotSize() ?? '-',
      [EMixpanelProperties.FILL_POLICY]: fillPolicy ? mapFillPolicyIntToText(fillPolicy, 'en_GB') : '-',
      [EMixpanelProperties.EXPIRATION]: expiration
        ? convertToDisplayUTC(dayjs(convertedExpiration).format('YYYY-MM-DD HH:mm:ss'))
        : '-',
      [EMixpanelProperties.TRADE_EXPIRY]: tradeExpiry ? tradeExpiry : '-',
      [EMixpanelProperties.PRICE]: price ? price : '-',
      [EMixpanelProperties.PENDING_PRICE]: priceTrigger ? priceTrigger : '-',
      [EMixpanelProperties.CURRENT_PRICE]: livePrice ? livePrice : currentPrice ?? 0,
      [EMixpanelProperties.TAKE_PROFIT]: takeProfit ? takeProfit : '-',
      [EMixpanelProperties.STOP_LOSS]: stopLoss ? stopLoss : '-',
      [EMixpanelProperties.UNITS]: units ? units : '-'
    };
  };

  public createDeleteOrderSubmittedProperties = ({ order, livePrice }: IDeleteOrderProperties): TMixpanelProperties => {
    const tradePreferenceProperties = placeOrderHelper.createTradePreferenceProperties();
    const deleteOrderProperties = this.deleteOrderProperties({ order, livePrice });
    const userAccountProperties = userHelper.createUserAccountProperties();
    return {
      ...deleteOrderProperties,
      ...tradePreferenceProperties,
      ...userAccountProperties
    };
  };
}

const closeOrderHelper = new CloseOrderHelper();
export default closeOrderHelper;
